/**
 * Theme configuration and runtime helpers.
 * All theme switching is via CSS custom properties — no React re-render needed.
 */

/** Available theme identifiers. */
export type ThemeName = 'light' | 'dark' | 'minimal' | 'colorful';

/**
 * Theme metadata for UI display. Display labels are pulled at render
 * time via `t('theme.options.<id>')`; the `id` + swatch are the only
 * static fields each row carries.
 */
export type ThemeMeta = {
  readonly id: ThemeName;
  /** Hex color for the swatch preview. */
  readonly swatch: string;
};

/** Ordered list of available themes. */
export const THEME_LIST: readonly ThemeMeta[] = [
  { id: 'light', swatch: '#2563eb' },
  { id: 'dark', swatch: '#3b82f6' },
  { id: 'minimal', swatch: '#18181b' },
  { id: 'colorful', swatch: '#c026d3' },
] as const;

/**
 * Apply a theme by setting `data-theme` on the document root.
 * Light theme is represented by removing the attribute (it's the CSS default).
 */
export function applyTheme(name: ThemeName): void {
  if (name === 'light') {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', name);
  }
}

/**
 * Chart color palettes per theme — used by ECharts instances.
 * Matches the CSS custom properties defined in tokens.css.
 */
export const CHART_PALETTES: Record<ThemeName, readonly string[]> = {
  light: ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2'],
  dark: ['#60a5fa', '#a78bfa', '#34d399', '#fbbf24', '#f87171', '#22d3ee'],
  minimal: ['#18181b', '#52525b', '#71717a', '#a1a1aa', '#d4d4d8', '#3f3f46'],
  colorful: ['#c026d3', '#7c3aed', '#2563eb', '#059669', '#e11d48', '#ea580c'],
};
